import React, { useEffect, useState } from "react";
import { BsSearch } from "react-icons/bs";
import "./SearchForm.scss";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  searchFormDataUpdated,
  isDataReleaseChangedUpdated,
} from "../../redux/reducer";
import {
  BATCH_POSITION,
  CATALOGUE,
  CUTOUT_SIZE,
  DEC,
  POSITION,
  RA,
  RADIUS,
  SEARCHBY,
  SOURCE,
  SPW,
} from "../../SearchConstants";
import {
  REGEXDECFORMAT1,
  REGEXDECFORMAT2,
  REGEXDECFORMAT3,
  REGEXRAFORMAT1,
  REGEXRAFORMAT2,
  REGEXRAFORMAT3,
} from "../../RegEx";
import {
  checkIfEmpty,
  checkIfObjectHasNonNullValues,
  isEmptyString,
} from "../../helperFunc";
import SearchSpwFIlter from "./SearchSpwFIlter";
import { BsInfoCircle } from "react-icons/bs";
import Tooltip from "../Tooltip/Tooltip";

const initialState = {
  ra: "",
  dec: "",
  radius: "",
  cutout_size: "initial",
  searchBy: POSITION,
};
const searchInputs = [
  {
    id: `${RA}`,
    type: "text",
    placeholder: "HH:MM:SS.S",
    displayOn: [SOURCE, POSITION],
    name: `${RA}`,
  },
  {
    id: `${DEC}`,
    type: "text",
    placeholder: "DD:MM:SS.S",
    displayOn: [SOURCE, POSITION],
    name: `${DEC}`,
  },
  {
    id: `${RADIUS}`,
    type: "number",
    placeholder: "Arcsecond",
    displayOn: [SOURCE],
    name: `${RADIUS}`,
  },
  {
    id: `${CUTOUT_SIZE}`,
    type: "number",
    default: "100",
    max: "500",
    placeholder: "Arcsecond",
    displayOn: [BATCH_POSITION],
    name: `${CUTOUT_SIZE}`,
  },
  {
    id: "file",
    type: "file",
    placeholder: "Upload file",
    displayOn: [BATCH_POSITION],
    name: "CSV",
    accept: ".csv",
  },
];

const initialValidationState = { ra: "", dec: "" };

function SearchForm({ file, setFile }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [validationError, setValidationError] = useState(
    initialValidationState
  );
  const { selectedSPW } = useSelector((state) => state.router);
  const dispatch = useDispatch();
  let queryParams = {};
  const ra = searchParams.get(RA);
  const dec = searchParams.get(DEC);
  if (ra && dec) {
    queryParams = {
      ra,
      dec,
      radius: initialState.radius,
      searchBy: POSITION,
    };
  }

  function handleFileUpload(event) {
    const file = event.target.files[0];
    setFile(file);
  }

  useEffect(() => {
    if (Object.keys(queryParams).length === 0) return;
    dispatch(
      searchFormDataUpdated({
        searchFormData: queryParams,
      })
    );
  }, []);

  const resetRadiusAndFile = () => {
    if (setFile) setFile(null);
    setFormData((prevFormData) => {
      return {
        ...prevFormData,
        radius: "",
      };
    });
  };

  const InitialFormValues = checkIfObjectHasNonNullValues(queryParams)
    ? { ...initialState, ...queryParams }
    : { ...initialState };
  const [formData, setFormData] = useState(InitialFormValues);

  useEffect(() => {
    if (
      formData.searchBy === BATCH_POSITION &&
      formData.cutout_size === "initial"
    )
      setFormData({ ...formData, cutout_size: "100" });
  }, [formData]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    if (type === "radio" && name === SEARCHBY) resetRadiusAndFile();
    setFormData((prevFormData) => {
      return {
        ...prevFormData,
        [name]: (name === CUTOUT_SIZE) & (value > 500) ? 500 : value,
      };
    });

    if (name === RA || name === DEC) {
      setValidationError((prevValidationError) => {
        return {
          ...prevValidationError,
          [name]: handleValidation(name, value),
        };
      });
    }
  };

  const handleValidation = (name, value) => {
    let result = false;
    const RARegEXF1 = new RegExp(REGEXRAFORMAT1);
    const RARegEXF2 = new RegExp(REGEXRAFORMAT2);
    const RARegEXF3 = new RegExp(REGEXRAFORMAT3);
    const DECRegEXF1 = new RegExp(REGEXDECFORMAT1);
    const DECRegEXF2 = new RegExp(REGEXDECFORMAT2);
    const DECRegEXF3 = new RegExp(REGEXDECFORMAT3);
    if (isEmptyString(value)) return "";
    if (name === RA) {
      result = RARegEXF1.test(value) || RARegEXF2.test(value) || RARegEXF3.test(value);
      return result
        ? ""
        : `Incorrect ${name} format.\n[Formats Supported: HH:MM:SS.S or hms or number]`;
    }
    if (name === DEC) {
      result = DECRegEXF1.test(value) || DECRegEXF2.test(value) || DECRegEXF3.test(value);
      return result
        ? ""
        : `Incorrect ${name} format.\n[Formats Supported: DD:MM:SS.S or dms or number ∈ [-90, 90]]`;
    }
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(
      searchFormDataUpdated({
        searchFormData: { ...formData, spw: selectedSPW },
      })
    );
    dispatch(
      isDataReleaseChangedUpdated({
        isDataReleaseChanged: false,
      })
    );
  };
  const reset = () => {
    setFormData(initialState);
    if (setFile) setFile(null);
    setValidationError(initialValidationState);
    dispatch(
      searchFormDataUpdated({
        searchFormData: initialState,
      })
    );
    dispatch(
      isDataReleaseChangedUpdated({
        isDataReleaseChanged: false,
      })
    );
    searchParams.delete("spw");
    setSearchParams(searchParams);
  };

  const checkIfSearchFormIsFilled = () => {
    if (isEmptyString(formData.searchBy)) return false;
    const isEmptyForCatalogue =
      !isEmptyString(formData.ra) &&
      !isEmptyString(formData.dec) &&
      !isEmptyString(formData.radius);

    const isEmptyForImage =
      !isEmptyString(formData.ra) && !isEmptyString(formData.dec);

    const isEmptyForBatchImage =
      !isEmptyString(formData.cutout_size) && file?.size;

    if (formData.searchBy === BATCH_POSITION) {
      return isEmptyForBatchImage;
    }
    return formData.searchBy === SOURCE ? isEmptyForCatalogue : isEmptyForImage;
  };

  const shouldSubmitBeDisabled =
    !checkIfSearchFormIsFilled() || !checkIfEmpty(validationError);

  const displayFormInputs = (searchInput, idx) => {
    return (
      <div className={`search-input-cont ${searchInput.type==='file' && 'flex-center'}`} key={idx}>
        <div>
          <BsSearch style={{ marginRight: "6px" }} />
          <label htmlFor={searchInput.id}>{searchInput.name}</label>
        </div>
        {searchInput.type === "file" ? (
          <>
            <input
              className="visually-hidden"
              type="file"
              id={searchInput.id}
              name={searchInput.name}
              onChange={handleFileUpload}
              accept={searchInput.accept}
            ></input>
            <a className="template-link" href="/templates/bulk_upload_template.csv">CSV Template</a>
          </>
        ) : (
          <input
            id={searchInput.id}
            type={searchInput.type}
            placeholder={searchInput.placeholder}
            value={formData[searchInput.name]}
            max={searchInput.name === CUTOUT_SIZE ? searchInput.max : undefined}
            onChange={handleChange}
            name={searchInput.name}
          />
        )}

        {validationError[searchInput.name] && (
          <div className="search-input-error">
            {validationError[searchInput.name]}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <form className="search-catalogue-container" onSubmit={handleSubmit}>
        <h3 className="search-catalogue-title">Search</h3>
        <div className="search-section">
          <div className="search-choose">
            <div>
              <label htmlFor="search-position">Image</label>
              <input
                type="radio"
                value={POSITION}
                name={SEARCHBY}
                onChange={handleChange}
                checked={formData.searchBy === POSITION}
                id="search-position"
              />
            </div>
            <div>
              <label htmlFor="search-source">Catalogue</label>
              <input
                type="radio"
                value={SOURCE}
                name={SEARCHBY}
                onChange={handleChange}
                checked={formData.searchBy === SOURCE}
                id="search-source"
              />
            </div>
            <div>
              <label htmlFor="search-batch-images">Bulk images (Max. 25)</label>
              <input
                type="radio"
                value={BATCH_POSITION}
                name={SEARCHBY}
                onChange={handleChange}
                checked={formData.searchBy === BATCH_POSITION}
                id="search-batch-images"
              />
            </div>
          </div>
          <div
            className={`search-bar-section ${
              formData.searchBy === SOURCE && "catalogue"
            }`}
          >
            <div className="search-inputs">
              {searchInputs.map((searchInput, idx) => {
                return searchInput.displayOn.includes(formData.searchBy) ? (
                  displayFormInputs(searchInput, idx)
                ) : (
                  <></>
                );
              })}
              {formData.searchBy === SOURCE && (
                <div className="search-input-spw">
                  <SearchSpwFIlter />
                </div>
              )}
            </div>
            <div className="search-buttons">
              <input
                type="submit"
                value="Search"
                disabled={shouldSubmitBeDisabled}
              />
              <button onClick={reset}>Reset</button>
            </div>
          </div>
        </div>
      </form>
    </>
  );
}

export default React.memo(SearchForm);
